import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import api from '../utils/api';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { 
  ExternalLink, TrendingUp, DollarSign, Truck, Briefcase, 
  Zap, Star, CheckCircle, ArrowRight, Filter
} from 'lucide-react';
import { toast } from 'sonner';

const ExternalJobBoards = () => {
  const [jobBoards, setJobBoards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');

  useEffect(() => {
    fetchJobBoards();
  }, []);

  const fetchJobBoards = async () => {
    try {
      const response = await api.get('/job-boards/list');
      setJobBoards(response.data);
    } catch (error) {
      toast.error('Failed to load job boards');
    } finally {
      setLoading(false);
    }
  };

  const categories = [
    { id: 'all', name: 'All Boards', icon: Truck },
    { id: 'premium', name: 'Premium Boards', icon: TrendingUp },
    { id: 'specialized', name: 'Specialized & Local', icon: Briefcase },
    { id: 'free', name: 'Free & Low-Cost', icon: Zap }
  ];

  const filteredBoards = selectedCategory === 'all' 
    ? jobBoards 
    : jobBoards.filter(board => board.category === selectedCategory);

  const getCategoryInfo = (category) => {
    switch(category) {
      case 'premium':
        return {
          title: 'Top Premium Boards (Highest Volume)',
          description: 'If you are running a serious carrier business, these paid subscriptions are often the most profitable due to the sheer volume of loads.',
          color: 'blue'
        };
      case 'specialized':
        return {
          title: 'Specialized & Local Boards',
          description: 'These platforms are specifically designed for the smaller equipment sizes used by box truck carriers and local deliveries.',
          color: 'green'
        };
      case 'free':
        return {
          title: 'Best Free & Low-Cost Options',
          description: 'If you are just starting and want to minimize overhead, these platforms offer free or affordable access to freight opportunities.',
          color: 'purple'
        };
      default:
        return {
          title: 'All Carrier Load Boards',
          description: 'Browse all available platforms to find the perfect fit for your carrier business needs.',
          color: 'slate'
        };
    }
  };

  const categoryInfo = getCategoryInfo(selectedCategory);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block h-12 w-12 animate-spin rounded-full border-4 border-solid border-blue-500 border-r-transparent"></div>
          <p className="mt-4 text-slate-600 font-medium">Loading job boards...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-slate-50">
      <Navbar />

      {/* Hero Section */}
      <section className="bg-gradient-to-r from-slate-900 to-slate-800 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center max-w-3xl mx-auto"
          >
            <h1 className="font-heading text-4xl sm:text-5xl font-bold mb-4">
              Carrier Load Board Directory
            </h1>
            <p className="text-xl text-slate-300">
              Connect with the best load boards for box truck and straight truck carriers
            </p>
          </motion.div>
        </div>
      </section>

      <main className="flex-1 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 w-full">
        {/* Category Filter */}
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-4">
            <Filter className="h-5 w-5 text-slate-600" />
            <h2 className="font-heading text-xl font-bold text-slate-900">Filter by Category</h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => setSelectedCategory(category.id)}
                className={`flex items-center gap-3 p-4 rounded-md border-2 transition-all ${
                  selectedCategory === category.id
                    ? 'border-blue-500 bg-blue-50 text-blue-700'
                    : 'border-slate-200 bg-white text-slate-700 hover:border-blue-300'
                }`}
                data-testid={`category-${category.id}`}
              >
                <category.icon className="h-5 w-5" />
                <span className="font-semibold text-sm">{category.name}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Category Description */}
        <motion.div
          key={selectedCategory}
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className={`bg-gradient-to-r from-${categoryInfo.color}-500 to-${categoryInfo.color}-600 text-white rounded-md p-6 mb-8 shadow-lg`}
        >
          <h2 className="font-heading text-2xl font-bold mb-2">{categoryInfo.title}</h2>
          <p className="text-lg opacity-90">{categoryInfo.description}</p>
        </motion.div>

        {/* Job Boards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {filteredBoards.length === 0 ? (
            <div className="col-span-full text-center py-12 bg-white border border-slate-200 rounded-md">
              <Truck className="h-12 w-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-600">No job boards found in this category.</p>
            </div>
          ) : (
            filteredBoards.map((board, index) => (
              <motion.div
                key={board.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className="bg-white border border-slate-200 rounded-md p-6 shadow-sm hover:shadow-md transition-all card-hover"
                data-testid={`job-board-${index}`}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <h3 className="font-heading text-xl font-bold text-slate-900">
                        {board.name}
                      </h3>
                      {board.is_verified && (
                        <CheckCircle className="h-5 w-5 text-blue-500" />
                      )}
                    </div>
                    {board.rating > 0 && (
                      <div className="flex items-center gap-1 mb-3">
                        <Star className="h-4 w-4 text-yellow-500 fill-yellow-500" />
                        <span className="text-sm font-semibold text-slate-700">{board.rating}</span>
                        <span className="text-xs text-slate-500">rating</span>
                      </div>
                    )}
                  </div>
                  <div className={`px-3 py-1 rounded-full text-xs font-medium ${
                    board.category === 'premium' ? 'bg-blue-100 text-blue-700' :
                    board.category === 'specialized' ? 'bg-green-100 text-green-700' :
                    'bg-purple-100 text-purple-700'
                  }`}>
                    {board.category === 'premium' ? 'Premium' :
                     board.category === 'specialized' ? 'Specialized' :
                     'Free/Low-Cost'}
                  </div>
                </div>

                <p className="text-slate-700 mb-4 leading-relaxed">{board.description}</p>

                <div className="mb-4">
                  <p className="text-sm font-semibold text-slate-900 mb-2">Best For:</p>
                  <p className="text-sm text-slate-600">{board.best_for}</p>
                </div>

                {board.pricing && (
                  <div className="flex items-center gap-2 mb-4 p-3 bg-slate-50 rounded-md border border-slate-200">
                    <DollarSign className="h-4 w-4 text-slate-600" />
                    <span className="text-sm font-medium text-slate-800">
                      {board.pricing}
                    </span>
                  </div>
                )}

                {board.payment_terms && (
                  <div className="flex items-center gap-2 mb-4 p-3 bg-green-50 rounded-md">
                    <DollarSign className="h-4 w-4 text-green-600" />
                    <span className="text-sm font-medium text-green-800">
                      Payment: {board.payment_terms}
                    </span>
                  </div>
                )}

                {board.features && board.features.length > 0 && (
                  <div className="mb-4">
                    <p className="text-sm font-semibold text-slate-900 mb-2">Key Features:</p>
                    <div className="space-y-1">
                      {board.features.slice(0, 3).map((feature) => (
                        <div key={feature} className="flex items-start gap-2">
                          <CheckCircle className="h-4 w-4 text-blue-500 mt-0.5 flex-shrink-0" />
                          <span className="text-sm text-slate-600">{feature}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {board.equipment_types && board.equipment_types.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-4">
                    {board.equipment_types.map((equipment) => (
                      <span
                        key={equipment}
                        className="inline-block px-2 py-1 bg-slate-100 text-slate-700 rounded text-xs font-medium"
                      >
                        {equipment}
                      </span>
                    ))}
                  </div>
                )}

                <a
                  href={board.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full bg-slate-900 text-white px-6 py-3 rounded-md font-semibold hover:bg-slate-800 transition-all btn-hover-lift flex items-center justify-center gap-2"
                  data-testid={`visit-board-${index}`}
                >
                  Visit {board.name}
                  <ExternalLink className="h-4 w-4" />
                </a>
              </motion.div>
            ))
          )}
        </div>

        {/* CTA Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-md p-8 mt-12 shadow-lg text-center"
        >
          <h2 className="font-heading text-2xl font-bold mb-2">
            Not Sure Where to Start?
          </h2>
          <p className="text-orange-100 mb-6">
            Get personalized job board recommendations based on your profile and experience.
          </p>
          <a
            href="/jobs"
            className="inline-block bg-white text-orange-600 px-8 py-3 rounded-md font-semibold hover:bg-orange-50 transition-all btn-hover-lift"
          >
            View Your Matched Jobs
            <ArrowRight className="inline ml-2 h-5 w-5" />
          </a>
        </motion.div>
      </main>

      <Footer />
    </div>
  );
};

export default ExternalJobBoards;
